import { useCallback, useMemo, useState } from "react";

import PropTypes from "prop-types";
import { compose } from "recompose";

import {
  trackProps,
  withTrack,
  withTrackProps,
} from "@dpdgroupuk/react-event-tracker";
import { withNotifier } from "@dpdgroupuk/mydpd-app";

import photoImg from "../../../../assets/icons/photo.svg";
import { PARCEL_HISTORY_COMPONENT } from "../../../../constants/analytics";
import {
  DASH,
  DOCUMENT,
  DOCUMENT_UNAVAILABLE,
  OK,
} from "../../../../constants/strings";
import { ParcelTypes } from "../../../../models/types";
import { getImageUrl, getParcelNumberByParcelEventCode } from "../../model";
import styles from "./ParcelHistory.module.scss";
import ImageView from "./ParcelImageView";
import { documentApi } from "../../../../apis";
import { isElectronEnvironment } from "../../../../utils/environment";
import { getVaultFilename } from "../../../EditDelivery/helpers";
import { downloadFile } from "../../../../utils/download";
import { withOverlay } from "@dpdgroupuk/mydpd-ui";

const ImageViewCell = ({
  row: rowData,
  onClickImage,
  onClickClose,
  notifier,
  overlay,
}) => {
  const [showImage, setShowImage] = useState(false);
  const images = useMemo(() => {
    const parcelNumber = getParcelNumberByParcelEventCode(
      rowData?.parcelEventCode || ""
    );

    return rowData.images.map(({ imageKey, imageType, imageCaption }) => ({
      src: getImageUrl(imageKey, imageType, parcelNumber),
      imageKey,
      title: imageCaption,
    }));
  }, [rowData]);

  const onClickDocumentLink = useCallback(
    overlay.showWhile(
      notifier.runAsync(async () => {
        try {
          const { data } = await documentApi.getVaultDocument({
            vaultDocumentKey: rowData.vaultDocumentKey,
          });

          // Convert base64 directly to blob using fetch data URL
          const response = await fetch(
            `data:${data.contentType};base64,${data.data}`
          );
          const blob = await response.blob();
          const fileURL = URL.createObjectURL(blob);

          if (isElectronEnvironment()) {
            downloadFile(
              fileURL,
              getVaultFilename(
                data.contentDisposition,
                rowData.vaultDocumentKey
              ),
              true
            );
            return;
          }

          window.open(fileURL, "_blank");
          setTimeout(() => URL.revokeObjectURL(fileURL), 1000);
        } catch (err) {
          throw new Error(DOCUMENT_UNAVAILABLE);
        }
      })
    ),
    [rowData.vaultDocumentKey, notifier]
  );

  const renderContent = useMemo(() => {
    if (rowData.vaultDocumentKey) {
      return (
        <a href="#" onClick={onClickDocumentLink}>
          {DOCUMENT}
        </a>
      );
    }

    if (images.length > 0) {
      return (
        <div className={styles.icon}>
          <img
            id="Image-view-cell-1"
            className="cursor-pointer"
            alt="img"
            src={photoImg}
            onClick={() => {
              onClickImage();
              setShowImage(true);
            }}
          />
        </div>
      );
    }

    return DASH;
  }, [
    rowData.vaultDocumentKey,
    images.length,
    onClickDocumentLink,
    onClickImage,
  ]);

  return (
    <>
      {renderContent}
      <ImageView
        images={images}
        show={showImage}
        carouselProps={{
          indicators: false,
        }}
        onClose={() => {
          onClickClose();
          setShowImage(false);
        }}
        buttonText={OK}
        parcel={rowData}
      />
    </>
  );
};

ImageViewCell.propTypes = {
  onClick: PropTypes.func,
  onClickClose: PropTypes.func,
  onClickImage: PropTypes.func,
  onClickNextImage: PropTypes.func,
  onClickPreviousImage: PropTypes.func,
  notifier: PropTypes.object,
  row: PropTypes.shape(ParcelTypes.ParcelEvent),
};

export default compose(
  withNotifier,
  withOverlay,
  withTrack(trackProps(PARCEL_HISTORY_COMPONENT.LOAD)),
  withTrackProps({
    onClickClose: PARCEL_HISTORY_COMPONENT.CLOSE,
    onClickImage: PARCEL_HISTORY_COMPONENT.IMAGE,
    onClickNextImage: PARCEL_HISTORY_COMPONENT.NEXT_IMAGE,
    onClickPreviousImage: PARCEL_HISTORY_COMPONENT.PREVIOUS_IMAGE,
  })
)(ImageViewCell);
